/* eslint-disable */
/**
 * Admin → Conversion panel.
 *
 * Three sections:
 *   1. Tier upgrade funnel - signups → 6 tiers (Free Trial → Connected)
 *   2. City-guide purchase funnel - published, grants, purchases, revenue, per-city
 *   3. Email vs in-app modal cohort - which surface drives more $4.99 purchases
 *
 * Brand:
 *   Golden Hour accent (#E8A830) on conversion rates
 *   Deep Ocean text (#0A1F2E) on Canvas background (#F9F6EF)
 */
import React, { useEffect, useState } from 'react';
import { TrendingUp, Users, MapPin, Mail, MousePointerClick, DollarSign, RefreshCw, Trophy, Sprout, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const TIER_DISPLAY = {
  free_trial: 'Free Trial',
  blueprint_only: 'Blueprint Only',
  essentials: 'Essentials',
  guided: 'Guided',
  community: 'Community',
  connected: 'Connected',
};

const AdminConversionPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('all');
  const [purging, setPurging] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState(null);

  const fetchData = async (rangeKey = range) => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/admin/conversion/funnels?range=${rangeKey}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(range); /* eslint-disable-next-line */ }, [range]);

  const purgeTestUsers = async () => {
    const sure = window.confirm(
      'Delete every preview/test user (emails matching test_/upsell_/modal_/utm_/cg_/direct_/open_)?\n\n' +
      'This is blocked on production (ENV=production guard). Cascades to listings, blueprints, ' +
      'entitlements, and city_guide_access. Cannot be undone.'
    );
    if (!sure) return;
    setPurging(true); setPurgeMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/purge-test-users`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (r.ok) {
        setPurgeMsg({ kind: 'success', text: `Purged ${d.purged} users (env=${d.env}).` });
        await fetchData(range);
      } else {
        setPurgeMsg({ kind: 'error', text: d.detail || `HTTP ${r.status}` });
      }
    } catch (e) {
      setPurgeMsg({ kind: 'error', text: e.message });
    } finally {
      setPurging(false);
    }
  };

  if (loading) return <p className="text-[#5A6B73]">Loading funnels…</p>;
  if (error) return <p className="text-red-700">Failed to load: {error}</p>;
  if (!data) return null;

  const { tier, cityGuide, cohort } = data;

  return (
    <div className="space-y-10" data-testid="admin-conversion-panel">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-display text-2xl text-[#0A1F2E] flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#E8A830]" /> Conversion
          </h2>
          <p className="text-sm text-[#5A6B73]">Live funnels computed from Mongo - no extra tracking infra.</p>
        </div>
        <div className="flex items-center flex-wrap gap-3">
          <div className="inline-flex items-center bg-white border border-[#EFE6CF] rounded-full p-1" data-testid="conversion-range-toggle">
            {['7d', '30d', '90d', 'all'].map((opt) => (
              <button
                key={opt}
                onClick={() => setRange(opt)}
                className={`px-3 py-1 text-xs font-mono uppercase tracking-widest rounded-full transition ${range === opt ? 'bg-[#0A1F2E] text-white' : 'text-[#1A5C6E]'}`}
                data-testid={`range-${opt}`}
              >{opt === 'all' ? 'All' : opt}</button>
            ))}
          </div>
          <button onClick={() => fetchData(range)} className="text-sm text-[#1A5C6E] hover:text-[#2CA8A0] flex items-center gap-1" data-testid="admin-conversion-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={purgeTestUsers}
            disabled={purging}
            className="text-xs px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 rounded-full disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="admin-purge-test-users"
          >
            <Trash2 className="w-3 h-3" /> {purging ? 'Purging…' : 'Purge test users'}
          </button>
        </div>
      </header>

      {purgeMsg && (
        <div className={`text-sm px-3 py-2 rounded ${purgeMsg.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="purge-result">{purgeMsg.text}</div>
      )}

      {/* SECTION 1 - TIER FUNNEL */}
      <section data-testid="tier-funnel">
        <h3 className="font-display text-xl text-[#0A1F2E] flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-[#2CA8A0]" /> Tier upgrade funnel</h3>
        <p className="text-xs text-[#5A6B73] mb-5">
          {tier.total_signups.toLocaleString()} signups in range · conversion rates anchored to the immediately prior step
        </p>
        <div className="bg-white rounded-2xl border border-[#EFE6CF] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F4ECD8]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Stage</th>
                <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Users</th>
                <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Conversion</th>
                <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Drop-off</th>
              </tr>
            </thead>
            <tbody>
              {tier.steps.map((s, i) => (
                <tr key={s.label} className="border-t border-[#EFE6CF]" data-testid={`tier-row-${s.label}`}>
                  <td className="px-4 py-3 text-sm text-[#0A1F2E] capitalize">
                    {s.label.startsWith('Signups') ? s.label : (TIER_DISPLAY[s.label] || s.label)}
                  </td>
                  <td className="px-4 py-3 text-right font-display text-base text-[#0A1F2E]">{s.count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono text-sm text-[#E8A830] font-semibold">{s.conversion_pct}%</span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-[#5A6B73]">{i === 0 ? '-' : `${s.drop_off_pct}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* SECTION 2 - CITY GUIDE FUNNEL */}
      <section data-testid="city-guide-funnel">
        <h3 className="font-display text-xl text-[#0A1F2E] flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 text-[#2CA8A0]" /> City-guide purchase funnel</h3>
        <p className="text-xs text-[#5A6B73] mb-5">Public preview pages are anonymous - preview-view tracking lands when GA4 is wired. For now: grants, modal triggers, and Stripe-completed purchases.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat label="Cities published" value={cityGuide.published_cities} icon={<MapPin className="w-4 h-4" />} />
          <Stat label="Auto grants (Guided)" value={cityGuide.auto_grants} icon={<Users className="w-4 h-4" />} />
          <Stat label="Modal triggered" value={cityGuide.modal_shown} icon={<MousePointerClick className="w-4 h-4" />} />
          <Stat label="Paid purchases" value={cityGuide.purchases} icon={<DollarSign className="w-4 h-4" />} accent />
        </div>

        <div className="bg-[#F4ECD8] rounded-2xl p-5 mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Modal → purchase</p>
            <p className="font-display text-3xl text-[#E8A830] font-semibold" data-testid="modal-to-purchase-pct">{cityGuide.modal_to_purchase_pct}%</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Total city-guide revenue</p>
            <p className="font-display text-3xl text-[#0A1F2E]" data-testid="city-guide-revenue-usd">${cityGuide.revenue_usd.toLocaleString()}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#EFE6CF] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F9F6EF]">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">City</th>
                <th className="text-right px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Auto</th>
                <th className="text-right px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Admin</th>
                <th className="text-right px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Paid</th>
                <th className="text-right px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {cityGuide.city_breakdown.map((c) => (
                <tr key={c.slug} className="border-t border-[#EFE6CF]" data-testid={`city-row-${c.slug}`}>
                  <td className="px-3 py-2 text-sm text-[#0A1F2E]">{c.name}</td>
                  <td className="px-3 py-2 text-right text-sm text-[#1A5C6E]">{c.guided_auto}</td>
                  <td className="px-3 py-2 text-right text-sm text-[#1A5C6E]">{c.admin_grant}</td>
                  <td className="px-3 py-2 text-right text-sm text-[#E8A830] font-semibold">{c.purchase}</td>
                  <td className="px-3 py-2 text-right font-display text-sm text-[#0A1F2E]">${c.revenue_usd.toLocaleString()}</td>
                </tr>
              ))}
              {cityGuide.city_breakdown.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-[#5A6B73]">No grants yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* SECTION 3 - COHORT EMAIL vs MODAL */}
      <section data-testid="cohort-funnel">
        <h3 className="font-display text-xl text-[#0A1F2E] flex items-center gap-2 mb-1"><Trophy className="w-4 h-4 text-[#2CA8A0]" /> Email vs modal - which converts</h3>
        <p className="text-xs text-[#5A6B73] mb-5">
          Compare the 24-hour Resend upsell vs the in-app modal triggered on the 3rd guide view.
          {(cohort.email.opens === null || cohort.email.opens === undefined) && (
            <span className="block mt-1 italic">Email open tracking unwired - counts reflect <em>sent</em> not <em>opened</em>. Hook Resend webhook to surface opens.</span>
          )}
        </p>

        <div className="grid md:grid-cols-2 gap-5">
          <CohortCard
            title="24-hour email"
            icon={<Mail className="w-5 h-5 text-[#1A5C6E]" />}
            metrics={[
              { label: 'Eligible (3rd view crossed)', value: cohort.email.eligible },
              { label: 'Email sent', value: cohort.email.sent },
              { label: 'Email opens', value: cohort.email.opens },
              { label: 'Open rate', value: `${cohort.email.open_rate_pct ?? 0}%` },
              { label: 'Purchases attributed', value: cohort.email.purchases },
            ]}
            convPct={cohort.email.conversion_pct}
            revenue={cohort.email.revenue_usd}
            isWinner={cohort.winner === 'email'}
            testId="cohort-email-card"
          />
          <CohortCard
            title="In-app modal"
            icon={<MousePointerClick className="w-5 h-5 text-[#1A5C6E]" />}
            metrics={[
              { label: 'Eligible (3rd view crossed)', value: cohort.modal.eligible },
              { label: 'Dismissed', value: cohort.modal.dismissed },
              { label: 'Dismiss rate', value: `${cohort.modal.dismiss_rate_pct}%` },
              { label: 'Purchases attributed', value: cohort.modal.purchases },
            ]}
            convPct={cohort.modal.conversion_pct}
            revenue={cohort.modal.revenue_usd}
            isWinner={cohort.winner === 'modal'}
            testId="cohort-modal-card"
          />
        </div>
        {cohort.winner !== 'tie' && (
          <p className="mt-4 text-sm text-[#0A1F2E]" data-testid="cohort-winner">
            <strong className="text-[#E8A830]">Current winner:</strong>{' '}
            {cohort.winner === 'modal' ? 'in-app modal' : '24-hour email'} -{' '}
            {cohort.winner === 'modal' ? cohort.modal.conversion_pct : cohort.email.conversion_pct}% conversion.
          </p>
        )}
      </section>

      {/* SECTION 4 - ACQUISITION COHORTS */}
      <section data-testid="cohorts-acquisition">
        <h3 className="font-display text-xl text-[#0A1F2E] flex items-center gap-2 mb-1"><Sprout className="w-4 h-4 text-[#2CA8A0]" /> Acquisition cohorts</h3>
        <p className="text-xs text-[#5A6B73] mb-5">
          Which traffic source produces which tier. Captured at signup from <code className="text-[10px]">?utm_source / ?utm_medium / ?utm_campaign</code> on the landing URL - 30-day persistence in localStorage. <strong>Direct</strong> = users who arrived without UTM params.
        </p>

        <div className="bg-white rounded-2xl border border-[#EFE6CF] overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-[#F4ECD8]">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Source</th>
                <th className="text-right px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Total</th>
                {(data.cohorts?.tier_columns || []).map((t) => (
                  <th key={t} className="text-right px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">{TIER_DISPLAY[t] || t}</th>
                ))}
                <th className="text-right px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#1A5C6E]">Paid %</th>
              </tr>
            </thead>
            <tbody>
              {(data.cohorts?.cohorts || []).map((c) => (
                <tr key={`${c.source}-${c.medium || ''}`} className="border-t border-[#EFE6CF]" data-testid={`cohort-row-${c.source_slug || c.source}`}>
                  <td className="px-3 py-2 text-sm text-[#0A1F2E]">
                    <p className="font-medium">{c.source}</p>
                    {c.medium && <p className="text-[10px] text-[#5A6B73] font-mono">{c.medium}</p>}
                  </td>
                  <td className="px-3 py-2 text-right font-display text-sm text-[#0A1F2E]">{c.total.toLocaleString()}</td>
                  {(data.cohorts?.tier_columns || []).map((t) => (
                    <td key={t} className="px-3 py-2 text-right text-sm text-[#1A5C6E]">{c.by_role[t] || 0}</td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <span className="font-mono text-sm text-[#E8A830] font-semibold">{c.paid_conversion_pct}%</span>
                  </td>
                </tr>
              ))}
              {(data.cohorts?.cohorts || []).length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-[#5A6B73]">No cohorts yet - they'll appear after the first UTM-tagged signup.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-[11px] font-mono text-[#5A6B73]">As of {new Date(tier.as_of).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</p>
    </div>
  );
};

const Stat = ({ label, value, icon, accent }) => (
  <div className={`bg-white rounded-xl p-4 border ${accent ? 'border-[#E8A830]/60' : 'border-[#EFE6CF]'}`}>
    <div className="flex items-center gap-2 text-[#1A5C6E] mb-2">
      {icon}
      <p className="text-[10px] font-mono uppercase tracking-widest">{label}</p>
    </div>
    <p className={`font-display text-3xl ${accent ? 'text-[#E8A830]' : 'text-[#0A1F2E]'}`}>{(value ?? 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</p>
  </div>
);

const CohortCard = ({ title, icon, metrics, convPct, revenue, isWinner, testId }) => (
  <div
    className={`bg-white rounded-2xl p-5 border ${isWinner ? 'border-[#E8A830] ring-2 ring-[#E8A830]/30' : 'border-[#EFE6CF]'}`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {icon}
        <h4 className="font-display text-lg text-[#0A1F2E]">{title}</h4>
      </div>
      {isWinner && (
        <span className="bg-[#E8A830] text-[#0A1F2E] text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold">Leading</span>
      )}
    </div>
    <ul className="space-y-2 mb-4">
      {metrics.map((m) => (
        <li key={m.label} className="flex items-center justify-between text-sm">
          <span className="text-[#5A6B73]">{m.label}</span>
          <span className="text-[#0A1F2E] font-medium">{typeof m.value === 'number' ? m.value.toLocaleString('en-US', { timeZone: 'America/Chicago' }) : m.value}</span>
        </li>
      ))}
    </ul>
    <div className="pt-4 border-t border-[#EFE6CF] flex items-center justify-between">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#1A5C6E]">Conversion</p>
        <p className="font-display text-3xl text-[#E8A830] font-semibold">{convPct}%</p>
      </div>
      <div className="text-right">
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#1A5C6E]">Revenue</p>
        <p className="font-display text-2xl text-[#0A1F2E]">${(revenue ?? 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</p>
      </div>
    </div>
  </div>
);

export default AdminConversionPanel;
